// The Microsoft 365 capabilities Boo can be given, over the generic capability
// picker helpers. No React and no api client at runtime, so `node --test`
// covers it.

import {
  choicesDiffer as differ,
  grantedChoices as granted,
  parseSavedChoices as parseSaved,
  scopesFor as scopes,
  type Capability,
  type Choices,
} from "./capability-access.ts";

export { levelOf, NO_SCOPES, sameChoice } from "./capability-access.ts";
export type { Capability, CapabilityLevel, Choice, Choices } from "./capability-access.ts";

export type Ms365CapabilityId = "files" | "teams" | "mail" | "calendar";

// Each level includes the access of the ones before it (Files.ReadWrite.All
// subsumes Files.Read.All, etc.). Every scope must be on the shared "Boo M365
// File Reader" app allowlist.
export const MS365_CAPABILITIES: ReadonlyArray<Capability<Ms365CapabilityId>> = [
  {
    id: "files",
    name: "OneDrive and SharePoint",
    levels: [
      {
        id: "read",
        label: "Read",
        summary: "Open and search files and sites.",
        scopes: ["Files.Read.All", "Sites.Read.All"],
      },
      {
        id: "edit",
        label: "Edit",
        summary: "Open, search, create, and edit files and sites.",
        scopes: ["Files.ReadWrite.All", "Sites.ReadWrite.All"],
      },
    ],
  },
  {
    id: "teams",
    name: "Teams history",
    levels: [
      {
        id: "read",
        label: "Read",
        summary: "Read the chats and channel messages the Boo account can see.",
        scopes: ["Chat.Read", "ChannelMessage.Read.All", "Team.ReadBasic.All", "Channel.ReadBasic.All"],
      },
    ],
  },
  {
    id: "mail",
    name: "Outlook mail",
    levels: [
      { id: "read", label: "Read", summary: "Read messages and attachments.", scopes: ["Mail.Read"] },
      {
        id: "send",
        label: "Send",
        summary: "Read, and send mail from the Boo account.",
        scopes: ["Mail.Read", "Mail.Send"],
      },
      {
        id: "manage",
        label: "Manage",
        summary: "Read, send, and organize: move, flag, and delete messages.",
        scopes: ["Mail.ReadWrite", "Mail.Send"],
      },
    ],
  },
  {
    id: "calendar",
    name: "Outlook calendar",
    levels: [
      { id: "read", label: "Read", summary: "See events and availability.", scopes: ["Calendars.Read"] },
      { id: "write", label: "Edit", summary: "See, create, and change events.", scopes: ["Calendars.ReadWrite"] },
    ],
  },
];

// Microsoft may report a granted scope in short (User.Read) or full-resource
// (https://graph.microsoft.com/User.Read) form; compare on the short name.
const shortScope = (s: string) => s.replace(/^https:\/\/graph\.microsoft\.com\//i, "");

export const grantedChoices = (grantedScopes: ReadonlyArray<string>): Choices =>
  granted(MS365_CAPABILITIES, grantedScopes.map(shortScope));
export const scopesFor = (choices: Choices): string[] => scopes(MS365_CAPABILITIES, choices);
export const choicesDiffer = (a: Choices, b: Choices) => differ(MS365_CAPABILITIES, a, b);
export const parseSavedChoices = (saved: unknown): Choices | null => parseSaved(MS365_CAPABILITIES, saved);
